import SortableCategoryItem from '@components/processMapAuxV2/SortableCategoryItem';
import CategoryContainer from '@components/processMapAuxV2/NodeCategoryContainer';
import { ProcessNode } from '@components/processMapAuxV2/NodeProcess';
import TimingNode from '@components/processMapAuxV2/NodeTiming';
import { TriggerNode } from '@components/processMapAuxV2/NodeTrigger';
import SearchFilterProcessMap from '@components/processMapAuxV2/SearchFilterProcessMap';
import WolfChip from '@components/ui/WolfChip';
import WolfDropdown from '@components/ui/WolfDropdown';
import WolfTooltip from '@components/ui/WolfTooltip';
import { graphQlClient } from '@config/graphqlClient';
import AreasGraphQL from '@graphql/area.queries';
import ProcessesGraphQL from '@graphql/process.queries';
import ServicesGraphQL from '@graphql/service.queries';
import { useLocationQuery } from '@hooks/useLocationQuery';
import { useTranslation } from '@hooks/useTranslation';
import { Area, getAreasRequest } from '@models/area.model';
import { StoredProcessMapState } from '@models/process-map.model';
import { getProcessesWithTriggeredByRequest, Process } from '@models/process.model';
import { getServicesRequest } from '@models/service.model';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Collapse, Typography } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { calculateNextYPosition } from '@utils/processMapv2/calculateNextYPosition';
import drawElementsV2 from '@utils/processMapv2/drawElementsV2';
import { EXPANDED_CONTAINER_BUFFER, EXPANDED_NODE_BUFFER } from '@utils/processMapv2/processMapConstants';
import { countAllSteps } from '@utils/processMapv2/processMapHelpers';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ReactFlow, { Controls, MarkerType, MiniMap, useReactFlow, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';

const PROCESS_MAP_STORAGE_KEY = 'processMapState';

const isUncategorizedProcess = (process: any, uncategorizedText: string = 'Uncategorized') => {
  return !process.category ||
         process.category === null ||
         process.category === undefined ||
         process.category === uncategorizedText ||
         process.category.trim() === '';
};

// Define nodeTypes outside component to prevent recreating on every render
const nodeTypes = {
  ProcessV2: ProcessNode,
  CategoryContainer: CategoryContainer,
  TriggerNode: TriggerNode,
  TimingNode: TimingNode,
};

const ProcessMapInner: React.FC = () => {
  const { organizationId } = useAppSelector((state: RootState) => state.organization);
  const reactFlowInstance = useReactFlow();

  const [areas, setAreas] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<Area | null>();
  const [services, setServices] = useState<any[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState<string>('');
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
  const [flowKey, setFlowKey] = useState<number>(0);
  const [drawnCategories, setDrawnCategories] = useState<
    { id: string; originalId: string; height: number; service: string; area: string; label: string }[]
  >([]);
  const [categoriesToDraw, setCategoriesToDraw] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const [elements, setElements] = useState<any[]>([]);
  const [edges, setEdges] = useState<any[]>([]);
  const [processes, setProcesses] = useState<any[]>([]);
  const localeProcMap = useTranslation('processMap');
  const localeCommon = useTranslation('common');

  // Configure drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const query = useLocationQuery();
  const history = useHistory();
  const [showMenu, setShowMenu] = useState(true);

  // Add a new state to track when initial data is loaded
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [savedStateLoaded, setSavedStateLoaded] = useState(false);

  // Function to clear query params when user manually adds categories
  const clearQueryParams = () => {
    const currentPath = window.location.pathname;
    history.replace(currentPath);
  };

  useEffect(() => {
    if (!isDataLoaded) return;
    const areaId = query.get('areaId');
    const serviceId = query.get('serviceId');
    const processId = query.get('processId');
    const tutorialMode = query.get('tutorial');

    if (areaId || serviceId || processId || tutorialMode) {
      // First we clear the existing map and the saved state in storage
      localStorage.removeItem(PROCESS_MAP_STORAGE_KEY);
      clearCurrentMap();
    }

    if (!isDataLoaded || !savedStateLoaded) return;

    // Tutorial mode: load all categories from all areas/services
    if (tutorialMode === 'true') {
      // Get all categories from all processes
      const allCategories: string[] = [];

      // Get regular categories
      const regularCategories = Array.from(
        new Set(
          processes
            .filter((process) => !isUncategorizedProcess(process, localeCommon['uncategorized']))
            .filter((process) => process.category)
            .map((process) => process?.service?.serviceId + '---' + process?.category),
        ),
      ).filter((category) => category);

      // Get uncategorized categories (one per service that has uncategorized processes)
      const servicesWithUncategorized = Array.from(
        new Set(
          processes
            .filter((process) => isUncategorizedProcess(process, localeCommon['uncategorized']))
            .map((process) => process?.service?.serviceId)
        )
      ).filter(Boolean);

      const uncategorizedCategories = servicesWithUncategorized.map(
        (serviceId) => `${serviceId}---${localeCommon['uncategorized']}`
      );

      allCategories.push(...regularCategories, ...uncategorizedCategories);

      if (allCategories.length > 0) {
        // Limit to 10 categories max in tutorial mode to avoid drawing too many
        const categoriesToDraw = allCategories.slice(0, 10);
        drawCategories(categoriesToDraw);
      }
      return;
    }

    // We draw all process in Area for the area, all services for the service, and the flow which includes it if a process Id is received
    if (areaId) {
      handleAreaChange([{ text: '', value: areaId }]);
      const areaProcesses = processes.filter((process) => process?.service?.areaId === areaId);

      // Get regular categories
      const regularCategories = Array.from(
        new Set(
          areaProcesses
            .filter((process) => !isUncategorizedProcess(process, localeCommon['uncategorized']))
            .map((process) => process?.service?.serviceId + '---' + process?.category),
        ),
      ).filter((category) => category);

      // Get uncategorized processes as individual categories
      const uncategorizedCategories = areaProcesses
        .filter((process) => isUncategorizedProcess(process, localeCommon['uncategorized']))
        .map((process) => `${process.service.serviceId}---${localeCommon['uncategorized']}`);
      
      // Remove duplicates and get unique uncategorized category IDs
      const uniqueUncategorizedCategories = Array.from(new Set(uncategorizedCategories));

      const allCategories = [...regularCategories, ...uniqueUncategorizedCategories];
      drawCategories(allCategories);
    } else if (serviceId) {
      const area = services.find((service) => service.serviceId === serviceId)?.areaId;
      handleAreaChange([{ text: '', value: area }]);
      handleServiceChange([{ text: '', value: serviceId }]);
      const serviceProcesses = processes.filter((process) => process?.service?.serviceId === serviceId);

      // Get regular categories
      const regularCategories = Array.from(
        new Set(
          serviceProcesses
            .filter((process) => !isUncategorizedProcess(process, localeCommon['uncategorized']))
            .map((process) => process?.service?.serviceId + '---' + process?.category),
        ),
      ).filter((category) => category);

      // Get uncategorized processes as individual categories
      const uncategorizedCategories = serviceProcesses
        .filter((process) => isUncategorizedProcess(process, localeCommon['uncategorized']))
        .map((process) => `${process.service.serviceId}---${localeCommon['uncategorized']}`);
      
      // Remove duplicates and get unique uncategorized category IDs
      const uniqueUncategorizedCategories = Array.from(new Set(uncategorizedCategories));

      const allCategories = [...regularCategories, ...uniqueUncategorizedCategories];
      drawCategories(allCategories);

      // handleCategoryChipClick({ text: '', value: serviceId + '---' + category });
    } else if (processId) {
      const process = processes.find((process) => process.processId === processId);
      if (process) {
        if (isUncategorizedProcess(process, localeCommon['uncategorized'])) {
          const uncategorizedCategoryId = `${process.service.serviceId}---${localeCommon['uncategorized']}`;
          drawCategories([uncategorizedCategoryId]);
        } else {
          const categoryId = `${process.service.serviceId}---${process.category}`;
          drawCategories([categoryId]);
        }
      }
    }
  }, [query, isDataLoaded, savedStateLoaded]);

  useEffect(() => {
    if (!isDataLoaded || !savedStateLoaded || !organizationId) return;

    const state: StoredProcessMapState = {
      organizationId,
      selectedAreaId,
      selectedServiceId,
      categoriesToDraw,
      selectedCategory,
    };

    // Get existing states array or create new one
    const existingData = localStorage.getItem(PROCESS_MAP_STORAGE_KEY);
    let stateCollection: StoredProcessMapState[] = [];

    if (existingData) {
      try {
        const parsed = JSON.parse(existingData);
        // Expect array format, throw error if not
        if (Array.isArray(parsed)) {
          stateCollection = parsed;
        } else {
          throw new Error('Invalid state format - expected array');
        }
      } catch (error) {
        console.error('Error parsing saved states, clearing storage:', error);
        localStorage.removeItem(PROCESS_MAP_STORAGE_KEY);
        stateCollection = [];
      }
    }

    // Find and replace state for current organization, or add new one
    const existingIndex = stateCollection.findIndex((s) => s.organizationId === organizationId);
    if (existingIndex >= 0) {
      stateCollection[existingIndex] = state;
    } else {
      stateCollection.push(state);
    }

    localStorage.setItem(PROCESS_MAP_STORAGE_KEY, JSON.stringify(stateCollection));
  }, [isDataLoaded, organizationId, selectedAreaId, selectedServiceId, categoriesToDraw, selectedCategory]);

  useEffect(() => {
    if (!savedStateLoaded || !isDataLoaded) return;

    // Clean up invalid categories from state
    const validCategories = categoriesToDraw.filter((cat) => {
      if (!cat || typeof cat !== 'string') return false;
      const parts = cat.split('---');
      return parts.length === 2 && parts[0] && parts[1];
    });

    if (validCategories.length !== categoriesToDraw.length) {
      setCategoriesToDraw(validCategories);
      setSelectedCategory(validCategories);
      return; // Skip this render cycle, will re-run with cleaned state
    }

    if (categoriesToDraw.filter(Boolean).length === 0) {
      setDrawnCategories([]);
      setElements([]);
      setEdges([]);
      return;
    }


    if (categoriesToDraw && categoriesToDraw.filter(Boolean).length > 0) {
      for (const category of categoriesToDraw) {
        if (!category || typeof category !== 'string') continue; // Skip null/undefined/invalid categories

        const parts = category.split('---');
        if (parts.length !== 2) continue; // Skip malformed category IDs

        const serviceId = parts[0];
        const categoryName = parts[1];

        if (!serviceId || !categoryName) continue; // Skip if either part is empty

        drawElementsGlobal(
          processes,
          services.filter((service) => service.serviceId === serviceId)[0]?.name,
          serviceId,
          categoryName,
          selectedArea?.name,
        );
      }
    }
  }, [categoriesToDraw, savedStateLoaded, processes, services, selectedArea]);

  useEffect(() => {
    // Skip if no organizationId yet
    if (!organizationId) {
      return;
    }

    const loadInitialData = async () => {
      try {
        // Reset data arrays when organization changes to prevent showing stale data
        // Don't clear map state (categoriesToDraw, selectedCategory, etc.) - let URL params or saved state handle that
        setIsDataLoaded(false);
        setSavedStateLoaded(false);
        setAreas([]);
        setServices([]);
        setProcesses([]);

        await Promise.all([getAreas(), getServices(), getAllProcesses()]);
        setIsDataLoaded(true);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    loadInitialData();
  }, [organizationId]);

  useEffect(() => {
    if (!isDataLoaded) return;
    const loadSavedState = async () => {
      if (!isDataLoaded || !organizationId || areas.length === 0 || services.length === 0 || processes.length === 0)
        return;

      try {
        const savedData = localStorage.getItem(PROCESS_MAP_STORAGE_KEY);
        if (savedData) {
          let stateCollection: StoredProcessMapState[] = [];

          try {
            const parsed = JSON.parse(savedData);
            // Expect array format, throw error if not
            if (Array.isArray(parsed)) {
              stateCollection = parsed;
            } else {
              throw new Error('Invalid state format - expected array');
            }
          } catch (parseError) {
            console.error('Error parsing saved states, clearing storage:', parseError);
            localStorage.removeItem(PROCESS_MAP_STORAGE_KEY);
            setSavedStateLoaded(true);
            return;
          }

          // Find state for current organization
          const parsedState = stateCollection.find((s) => s.organizationId === organizationId);

          if (!parsedState) {
            setSavedStateLoaded(true);
            return;
          }

          // Validate that saved area and service still exist
          let validAreaId = parsedState.selectedAreaId;
          let validServiceId = parsedState.selectedServiceId;

          // Check if saved area exists
          if (parsedState.selectedAreaId) {
            const areaExists = areas.find((area) => area.areaId === parsedState.selectedAreaId);
            if (!areaExists) {
              console.warn(`Saved area ${parsedState.selectedAreaId} no longer exists, clearing area selection`);
              validAreaId = '';
            }
          }

          // Check if saved service exists
          if (parsedState.selectedServiceId) {
            const serviceExists = services.find((service) => service.serviceId === parsedState.selectedServiceId);
            if (!serviceExists) {
              console.warn(`Saved service ${parsedState.selectedServiceId} no longer exists, clearing service selection`);
              validServiceId = '';
            }
          }

          // Filter out invalid categories instead of clearing entire state
          let validCategories = parsedState.categoriesToDraw || [];
          const invalidCategories: string[] = [];

          if (parsedState.categoriesToDraw && parsedState.categoriesToDraw.length > 0) {
            validCategories = parsedState.categoriesToDraw.filter((categoryId) => {
              if (!categoryId) return false;

              const [serviceId, categoryName] = categoryId.split('---');

              // Check if service exists
              const serviceExists = services.find((service) => service.serviceId === serviceId);
              if (!serviceExists) {
                invalidCategories.push(categoryId);
                return false;
              }

              // Check if category/processes exist
              if (categoryName === localeCommon['uncategorized']) {
                // Check if there are uncategorized processes for this service
                const uncategorizedProcesses = processes.filter(
                  (process) => process?.service?.serviceId === serviceId && isUncategorizedProcess(process, localeCommon['uncategorized'])
                );
                if (uncategorizedProcesses.length === 0) {
                  invalidCategories.push(categoryId);
                  return false;
                }
              } else {
                // Check if regular category processes exist
                const categoryProcesses = processes.filter(
                  (process) => process?.service?.serviceId === serviceId && process.category === categoryName,
                );
                if (categoryProcesses.length === 0) {
                  invalidCategories.push(categoryId);
                  return false;
                }
              }

              return true;
            });
          }

          // Update saved state if anything was invalid
          const needsUpdate =
            invalidCategories.length > 0 ||
            validAreaId !== parsedState.selectedAreaId ||
            validServiceId !== parsedState.selectedServiceId;

          if (needsUpdate) {
            if (invalidCategories.length > 0) {
              console.warn('Some saved categories no longer exist and were removed:', invalidCategories);
            }

            // Update saved state with only valid items
            const updatedState = {
              ...parsedState,
              selectedAreaId: validAreaId,
              selectedServiceId: validServiceId,
              categoriesToDraw: validCategories,
              selectedCategory: validCategories,
            };

            // Update storage with cleaned state
            const existingIndex = stateCollection.findIndex((s) => s.organizationId === organizationId);
            if (existingIndex >= 0) {
              stateCollection[existingIndex] = updatedState;
              localStorage.setItem(PROCESS_MAP_STORAGE_KEY, JSON.stringify(stateCollection));
            }
          }

          // Restore the validated state (only valid items)
          setSelectedAreaId(validAreaId);
          setSelectedArea(areas.find((area) => area.areaId === validAreaId) || null);
          setSelectedServiceId(validServiceId);
          setSelectedCategory(validCategories);

          // Add valid categories to draw
          if (validCategories.length > 0) {
            addProcessCategories(categoriesToDraw, { ...parsedState, categoriesToDraw: validCategories });
          }
        }
        setSavedStateLoaded(true);
      } catch (error) {
        console.error('Error loading saved process map state:', error);
        localStorage.removeItem(PROCESS_MAP_STORAGE_KEY);
        setSavedStateLoaded(true);
      }
    };

    loadSavedState();
  }, [isDataLoaded, organizationId, areas, services, processes]);

  async function addProcessCategories(categoriesToDraw: any[], parsedState: any) {
    for (const category of parsedState.categoriesToDraw) {
      await new Promise((resolve) => {
        setTimeout(() => {
          if (!categoriesToDraw.includes(category)) {
            setCategoriesToDraw((prev) => [...prev, category]);
          }
          resolve(void 0);
        }, 100);
      });
    }
  }

  const getAreas = async () => {
    try {
      const data: getAreasRequest = await graphQlClient.request(AreasGraphQL.queries.getAreas);
      const sortedAreas = data.getAreas.sort((a, b) => (a.order > b.order ? 1 : -1));
      setAreas(sortedAreas);

      // Only set default area if no saved state
      if (!localStorage.getItem(PROCESS_MAP_STORAGE_KEY)) {
        setSelectedAreaId('');
        setSelectedArea(null);
      }
    } catch (e: any) {
      console.error('Error loading areas:', e);
    }
  };

  const getServices = async () => {
    try {
      const data: getServicesRequest = await graphQlClient.request(ServicesGraphQL.queries.getServices);
      setServices(data.getServices);

      // Only set default service if no saved state
      if (!localStorage.getItem(PROCESS_MAP_STORAGE_KEY)) {
        setSelectedServiceId('');
      }
    } catch (e: any) {
      console.error('Error loading services:', e);
    }
  };

  const getAllProcesses = async () => {
    try {
      const data: getProcessesWithTriggeredByRequest = await graphQlClient.request(
        ProcessesGraphQL.queries.getProcessesWithTriggeredBy,
      );
      setProcesses(data?.getProcessesWithTriggeredBy);

      if (!localStorage.getItem(PROCESS_MAP_STORAGE_KEY)) {
      }
    } catch (e: any) {
      console.error('Error loading processes:', e);
    }
  };

  const resetFlow = () => {
    setFlowKey((prevKey) => prevKey + 1);
  };

  const clearCurrentMap = () => {
    setCategoriesToDraw(() => []);
    setDrawnCategories(() => []);
    setElements(() => []);
    setEdges(() => []);
    setExpandedCategories(() => new Set());
    resetFlow();
  };

  const drawCategories = async (categories: string[]) => {
    for (const item of categories) {
      await new Promise((resolve) => {
        setTimeout(() => {
          setCategoriesToDraw((prev) => [...prev, item]);
          resolve(void 0);
        }, 100);
      });
    }
  };

  const handleAreaChange = (updatedAreas: { text: string; value: string }[]) => {
    if (updatedAreas.length === 0) {
      setSelectedAreaId('');
    } else {
      setSelectedAreaId(updatedAreas[0].value);
      setSelectedArea(areas.filter((area) => area.areaId === updatedAreas[0].value)[0]);
    }
    setSelectedServiceId('');
  };

  const handleServiceChange = (updatedServices: { text: string; value: string }[]) => {
    if (updatedServices.length === 0) {
      setSelectedServiceId('');
    } else {
      setSelectedServiceId(updatedServices[0].value);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    // Normalize categoryId: if it's in old format (full UUID), convert to identifier
    const parts = categoryId.split('---');
    const serviceId = parts[0];
    const category = parts[1] || '';
    const normalizedId = serviceId.length === 8 ? categoryId : serviceId.slice(0, 8) + '---' + category;

    // Find the full category info from drawnCategories
    const categoryToRemove = drawnCategories.find((cat) => cat.id === normalizedId);
    if (!categoryToRemove) return;

    // Remove from tracking states
    setSelectedCategory((prev) => prev.filter((category) => category !== categoryToRemove.originalId));

    // Remove from categoriesToDraw and trigger redraw
    const remainingCategories = categoriesToDraw.filter((category) => category !== categoryToRemove.originalId);

    // Clear current map first
    clearCurrentMap();

    // Re-draw remaining categories to ensure proper positioning
    for (const category of remainingCategories) {
      await new Promise((resolve) => {
        setTimeout(() => {
          setCategoriesToDraw((prev) => [...prev, category]);
          resolve(void 0);
        }, 100);
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    // Find indices in drawnCategories
    const oldIndex = drawnCategories.findIndex((cat) => cat.id === active.id);
    const newIndex = drawnCategories.findIndex((cat) => cat.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder categoriesToDraw array using originalIds
    const newCategoriesToDraw = arrayMove(categoriesToDraw, oldIndex, newIndex);

    // Clear current map first
    clearCurrentMap();

    // Re-draw all categories in new order
    for (const category of newCategoriesToDraw) {
      await new Promise((resolve) => {
        setTimeout(() => {
          setCategoriesToDraw((prev) => [...prev, category]);
          resolve(void 0);
        }, 100);
      });
    }
  };

  const drawElementsGlobal = async (
    allData: Process[],
    serviceName: string,
    selectedServiceId: string,
    selectedCategory: string,
    areaName?: string,
  ) => {
    // Check if this is an uncategorized process category
    const isUncategorizedCategory = selectedCategory === localeCommon['uncategorized'];
    
    let categoryExists;
    let data;

    if (isUncategorizedCategory) {
      // For uncategorized processes, find all uncategorized processes for this service
      const uncategorizedProcesses = allData.filter(
        (process) => process?.service?.serviceId === selectedServiceId && isUncategorizedProcess(process, localeCommon['uncategorized'])
      );
      categoryExists = uncategorizedProcesses.length > 0;
      data = uncategorizedProcesses;
    } else {
      // Regular category validation and filtering
      categoryExists = allData.some(
        (process) => process?.service?.serviceId === selectedServiceId && process.category === selectedCategory,
      );
      data = allData
        .filter((item) => item?.service?.serviceId === selectedServiceId)
        .filter((item) => item?.category === selectedCategory);
    }

    if (!categoryExists) {
      console.warn(`Category ${selectedCategory} no longer exists for service ${selectedServiceId}`);
      // Remove the invalid category from tracking
      const invalidCategoryId = `${selectedServiceId}---${selectedCategory}`;
      setCategoriesToDraw((prev) => {
        const filtered = prev.filter((cat) => cat && cat !== invalidCategoryId);
        // Only update if actually changed to prevent infinite loop
        return filtered.length !== prev.length ? filtered : prev;
      });
      setSelectedCategory((prev) => {
        const filtered = prev.filter((cat) => cat && cat !== invalidCategoryId);
        // Only update if actually changed to prevent infinite loop
        return filtered.length !== prev.length ? filtered : prev;
      });
      return;
    }

    // This is needed to clear the drawing
    resetFlow();

    const identifier = selectedServiceId.slice(0, 8) + '---' + selectedCategory;

    // If we go this route to accumulate categories, we need to check if we are already drawing that category
    if (drawnCategories.some((item) => item.id === identifier)) {
      return;
    }

    const startingVerticalOffset = drawnCategories?.reduce((acc, curr) => acc + curr.height, 0);

    const drawnCategory = data;
    if (!drawnCategory || drawnCategory.length === 0) {
      return;
    }

    const { nodes, edges: newDrawnEdges } = drawElementsV2(
      drawnCategory,
      startingVerticalOffset || 0,
      identifier,
      processes,
      localeCommon['uncategorized'],
      localeProcMap['noConnectionsWarning'],
      localeProcMap['noTriggerDefined'],
      localeProcMap['warning'],
    );

    // Update container nodes with expanded state
    const isFirstCategory = drawnCategories.length === 0;
    const updatedNodes = nodes.map((node: any) => {
      if (node.type === 'CategoryContainer') {
        return {
          ...node,
          data: {
            ...node.data,
            expanded: expandedCategories.has(identifier),
            // Add tutorial ID to the first category container
            tutorialId: isFirstCategory ? 'process-map-first-category' : undefined,
          },
        };
      }
      return node;
    });

    let newNodes = [...elements, ...updatedNodes.filter((item: any) => item !== undefined)];
    let newEdges = [...edges, ...newDrawnEdges.filter((item: any) => item !== undefined)];

    // Filter out nodes and edges for categories that should be removed
    for (const category of drawnCategories) {
      if (!categoriesToDraw.includes(category.originalId)) {
        newNodes = newNodes.filter((item: any) => item.id !== 'container-' + category.id);
        newNodes = newNodes.filter((item: any) => !item.id.startsWith(category.id));
        newEdges = newEdges.filter(
          (item: any) => item.source !== 'container-' + category.id && item.target !== 'container-' + category.id,
        );
        newEdges = newEdges.filter((item: any) => !item.id.startsWith(category.id));
        setDrawnCategories((prev) => prev.filter((item) => item.id !== category.id));
      }
    }

    setElements(newNodes);
    setEdges(newEdges);

    const totalHeight = nodes.find((item: any) => item.id === 'container-' + identifier)?.data?.height || 0;

    let displayLabel: string;
    if (isUncategorizedCategory) {
      displayLabel = localeCommon['uncategorized'];
    } else {
      displayLabel = selectedCategory;
    }

    // Get the actual area from the first process's service instead of using the selected area
    const actualAreaName = data.length > 0 ? data[0]?.service?.area?.name || '' : areaName || '';

    setDrawnCategories((prev) => [
      ...prev,
      {
        id: identifier,
        originalId: selectedServiceId + '---' + selectedCategory,
        height: totalHeight + 100,
        service: serviceName,
        area: actualAreaName,
        label: displayLabel,
      },
    ]);
  };

  // **** CLICK HANDLERS ****

  const onNodeClick = (event: any) => {
    // Look through the event path to find the element with our data attributes
    const path = event.nativeEvent.composedPath();
    const expandCategory = path.find((element: any) => element.dataset && element.dataset.action === 'category-expand');

    if (expandCategory) {
      // categoryId is now the identifier (already in format: serviceId8chars---category)
      handleCategoryExpand(expandCategory.dataset.categoryId, true);
      return;
    }

    const collapseCategory = path.find(
      (element: any) => element.dataset && element.dataset.action === 'category-collapse',
    );

    if (collapseCategory) {
      // categoryId is now the identifier (already in format: serviceId8chars---category)
      handleCategoryExpand(collapseCategory.dataset.categoryId, false);
      return;
    }

    const expandProcess = path.find(
      (element: any) => element.dataset && element.dataset.action === 'individual-expand',
    );
    if (expandProcess) {
      handleProcessExpand(expandProcess.dataset.id, expandProcess.dataset.expanded);
      return;
    }

    const addCategory = path.find((element: any) => element.dataset && element.dataset.action === 'category-add');
    if (addCategory) {
      handleCategoryAdd(addCategory.dataset.id);
      return;
    }

    const removeCategory = path.find((element: any) => element.dataset && element.dataset.action === 'category-remove');
    if (removeCategory) {
      // categoryId is now the identifier (already in format: serviceId8chars---category)
      handleDeleteCategory(removeCategory.dataset.categoryId);
      return;
    }

    const showCategoryLink = path.find(
      (element: any) => element.dataset && element.dataset.action === 'category-show-link',
    );
    if (showCategoryLink) {
      handleCategoryLink(showCategoryLink.dataset.origin, showCategoryLink.dataset.id);
      return;
    }
  };

  const onEdgeClick = (_event: any, edge: any) => {
    const connectedNodes = [edge.source, edge.target];

    if (edge.id.startsWith('temp-link')) {
      return;
    }

    const newNodes = elements.map((node) => ({
      ...node,
      style: connectedNodes.includes(node.id) ? { border: '2px solid red' } : {},
    }));

    setElements(newNodes);
  };

  const onPaneClick = () => {
    const newNodes = elements.map((node) => ({
      ...node,
      style: {},
    }));
    setElements(newNodes);
  };

  const areaList = areas.map((area) => ({
    text: area.name,
    value: area.areaId,
  }));

  const serviceList = services
    .filter((item) => item.areaId === selectedAreaId)
    .map((service) => ({
      text: service.name,
      value: service.serviceId,
    }));

  const serviceProcesses = processes.filter((process) => process.service.serviceId === selectedServiceId);

  // Get regular categories
  const uniqueCategories = Array.from(
    new Set(
      serviceProcesses
        .filter((process) => !isUncategorizedProcess(process))
        .filter((process) => process.category)
        .map((process) => process.category),
    ),
  );

  // Get uncategorized processes
  const uncategorizedProcesses = serviceProcesses.filter((process) => isUncategorizedProcess(process));

  const categoryList = [
    // Regular categories
    ...uniqueCategories.map((category) => ({
      text: category,
      value: `${selectedServiceId}---${category}`,
    })),
    // Add single "Uncategorized" category if there are uncategorized processes
    ...(uncategorizedProcesses.length > 0 ? [{
      text: localeCommon['uncategorized'],
      value: `${selectedServiceId}---${localeCommon['uncategorized']}`,
    }] : [])
  ];

  const handleSearchResult = (result: any) => {
    if (result.type === 'Category') {
      // Clear query params when user manually adds a category
      clearQueryParams();
      // Format the category ID to match the chip format
      const categoryId = result.serviceId + '---' + result.name;

      // Add the category to draw if not already present
      setCategoriesToDraw((prev) => {
        if (prev.includes(categoryId)) return prev;
        return [...prev, categoryId];
      });

      setSelectedCategory((prev) => {
        if (prev.includes(categoryId)) return prev;
        return [...prev, categoryId];
      });
    }

    if (result.type === 'Service') {
      // First set the area
      const service = services.find((s) => s.serviceId === result.id);
      if (!service) return;

      // Set the area if it's different
      if (selectedAreaId !== service.areaId) {
        setSelectedAreaId(service.areaId);
        setSelectedArea(areas.find((area) => area.areaId === service.areaId) || null);
      }

      setSelectedServiceId(result.id);
      setShowMenu(true);
    }

    if (result.type === 'Area') {
      setSelectedAreaId(result.id);
      setSelectedArea(areas.find((area) => area.areaId === result.id) || null);
      setSelectedServiceId('');
      setShowMenu(true);
    }
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleCategoryLink = (origin: string, target: string) => {
    const tempEdgeId = `temp-link-${origin}-${target}`;

    // Check if the edge already exists
    const existingEdge = edges.find((edge) => edge.id === tempEdgeId);

    if (existingEdge) {
      // Remove the edge and clean up highlighting
      setEdges((prev) => prev.filter((edge) => edge.id !== tempEdgeId));
      setElements(elements.map((node) => ({ ...node, style: {} })));
      return;
    }

    const originNode = elements.find((node) => node.id === origin);
    const targetContainerId = 'container-' + target.slice(0, 8) + '---' + target.split('---')[1];
    const targetContainer = elements.find((node) => node.id === targetContainerId);

    // Only proceed if both nodes exist
    if (!originNode || !targetContainer) {
      console.warn('Origin or target node not found');
      return;
    }

    // Highlight the connected nodes
    const connectedNodes = [originNode.id, targetContainer.id];
    const newNodes = elements.map((node) => ({
      ...node,
      style: connectedNodes.includes(node.id) ? { border: '2px solid #2196f3' } : {},
    }));

    // Create a temporary edge between the nodes
    const newEdge = {
      id: tempEdgeId,
      source: origin,
      target: targetContainerId,
      type: 'smoothstep',
      style: { stroke: '#2196f3', strokeWidth: 2 },
      markerEnd: {
        type: MarkerType.Arrow,
        width: 30,
        height: 20,
      },
    };

    setElements(newNodes);
    setEdges((prev) => [...prev, newEdge]);

    // Zoom to the target container
    if (targetContainer && reactFlowInstance) {
      const padding = 100;
      const x = targetContainer.position.x;
      const y = targetContainer.position.y;
      const width = targetContainer.data?.width || 1000;
      const height = targetContainer.data?.height || 500;

      reactFlowInstance.fitBounds(
        {
          x: x - padding,
          y: y - padding,
          width: width + padding * 2,
          height: height + padding * 2,
        },
        { duration: 800 }
      );
    }
  };

  const handleCategoryExpand = (catId: string, isExpanding: boolean) => {
    const serviceId = catId.split('---')[0];
    const category = catId.split('---')[1];
    // If serviceId is already 8 chars, it's already an identifier; otherwise shorten it
    const identifier = serviceId.length === 8 ? catId : serviceId.slice(0, 8) + '---' + category;
    const containerNode = elements.find((node: any) => node.id === `container-${identifier}`);

    if (!containerNode) return;

    // Update the expanded state
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (isExpanding) {
        newSet.add(identifier);
      } else {
        newSet.delete(identifier);
      }
      return newSet;
    });

    // First: Get all nodes that need to be updated
    const nodesInContainer = elements.filter(
      (node: any) => node.parentNode === containerNode.id && node.id !== containerNode.id,
    );

    // Second: Calculate new positions for process nodes and build a position map
    const processPositionMap = new Map<string, { oldPosition: any; newPosition: any; oldX: number; newX: number }>();

    const updatedNodes = elements
      .filter((item: any) => item.parentNode === containerNode.id || item.id.startsWith(identifier))
      .map((node: any) => {
        if (node.type === 'ProcessV2' && !node.data.isExternal && node.id.startsWith(identifier)) {
          const nodeIsExpanding = !node.data.expand;
          const newY = nodeIsExpanding
            ? calculateNextYPosition(
                elements.filter((item: any) => item.parentNode === containerNode.id),
                node.position.x,
                node.position.y,
              )
            : node.data.originalY;

          // Track position change for this process
          processPositionMap.set(node.id, {
            oldPosition: node.position,
            newPosition: { x: node.position.x, y: newY },
            oldX: node.position.x,
            newX: node.position.x,
          });

          return {
            ...node,
            data: {
              ...node.data,
              expand: nodeIsExpanding,
            },
            position: {
              ...node.position,
              y: newY,
            },
          };
        }
        if (
          (node.type === 'TriggerNode' || node.type === 'ProcessV2') &&
          node.id.startsWith(identifier + '-trigger') &&
          node.position.y > 100 &&
          node.position.x > 100
        ) {
          const nodeIsExpanding = !node.data.expand;
          const newY = nodeIsExpanding
            ? calculateNextYPosition(elements, node.position.x, node.position.y)
            : node.data.originalY;

          // Track position change for trigger nodes
          processPositionMap.set(node.id, {
            oldPosition: node.position,
            newPosition: { x: node.position.x, y: newY },
            oldX: node.position.x,
            newX: node.position.x,
          });

          return {
            ...node,
            data: {
              ...node.data,
              expand: nodeIsExpanding,
            },
            position: {
              ...node.position,
              y: newY,
            },
          };
        }
        if (node.type === 'ProcessV2' && node.data.isExternal) {
          const newY = isExpanding ? node.position.y + 100 : node.data.originalY;

          // Track position change for external processes
          processPositionMap.set(node.id, {
            oldPosition: node.position,
            newPosition: { x: node.position.x, y: newY },
            oldX: node.position.x,
            newX: node.position.x,
          });

          return {
            ...node,
            position: {
              ...node.position,
              y: newY,
            },
          };
        }
        return node;
      });

    // 2.5: Update TimingNode positions based on their connected processes
    const updatedTimingNodes = elements
      .filter((node: any) => node.type === 'TimingNode' && node.parentNode === containerNode.id)
      .map((timingNode: any) => {
        // Find edges connected to this timing node
        const incomingEdge = edges.find((edge: any) => edge.target === timingNode.id);
        const outgoingEdge = edges.find((edge: any) => edge.source === timingNode.id);

        if (!incomingEdge || !outgoingEdge) {
          return timingNode; // Keep timing node unchanged if edges not found
        }

        const sourceNodeId = incomingEdge.source;
        const targetNodeId = outgoingEdge.target;

        // Get source and target nodes (check both original and updated)
        const sourceNodeUpdate = processPositionMap.get(sourceNodeId);
        const targetNodeUpdate = processPositionMap.get(targetNodeId);
        if (targetNodeUpdate?.newPosition?.y && isExpanding) {
          targetNodeUpdate.newPosition.y = targetNodeUpdate.newPosition.y + EXPANDED_NODE_BUFFER;
        }

        const sourceNode = updatedNodes.find((n: any) => n.id === sourceNodeId) ||
                          elements.find((n: any) => n.id === sourceNodeId);
        const targetNode = updatedNodes.find((n: any) => n.id === targetNodeId) ||
                          elements.find((n: any) => n.id === targetNodeId);

        if (!sourceNode || !targetNode) {
          return timingNode; // Keep timing node unchanged if source/target not found
        }

        // Only update if either source or target moved
        if (!sourceNodeUpdate && !targetNodeUpdate) {
          return timingNode; // Neither node moved, keep timing node as is
        }

        // Calculate new midpoint position
        const sourcePos = sourceNodeUpdate ? sourceNodeUpdate.newPosition : sourceNode.position;
        const targetPos = targetNodeUpdate ? targetNodeUpdate.newPosition : targetNode.position;

        const midX = (sourcePos.x + 250 + targetPos.x) / 2;
        const midY = (sourcePos.y + targetPos.y + 102) / 2;

        return {
          ...timingNode,
          position: {
            x: midX,
            y: midY - 40, // Offset above the connection line
          },
        };
      });

    // Merge timing nodes into updatedNodes
    const allUpdatedNodes = [...updatedNodes];
    updatedTimingNodes.forEach((timingNode: any) => {
      const existingIndex = allUpdatedNodes.findIndex((n: any) => n.id === timingNode.id);
      if (existingIndex >= 0) {
        allUpdatedNodes[existingIndex] = timingNode;
      } else {
        allUpdatedNodes.push(timingNode);
      }
    });

    // Third: Calculate new container height
    let newContainerHeight = 0;
    if (isExpanding) {
      const maxY = Math.max(
        ...nodesInContainer.map((node: any) => {
          const nodeHeight = !node.data?.expand ? countAllSteps(node.data?.steps) * 50 : 0;
          return node.position.y + nodeHeight;
        }),
      );
      newContainerHeight = Math.max(maxY, containerNode.data.height) + EXPANDED_CONTAINER_BUFFER;
    } else {
      newContainerHeight = containerNode.data.originalHeight;
    }

    // Fourth: Update container with new height and expanded state
    const updatedContainer = {
      ...containerNode,
      data: {
        ...containerNode.data,
        height: newContainerHeight,
        expanded: isExpanding,
      },
    };

    // Fifth: Calculate positions for containers below if needed
    const containersBelow = elements
      .filter((item: any) => item.id !== updatedContainer.id)
      .filter((item: any) => item.id.startsWith('container-'))
      .filter((item) => item.position.y > containerNode.position.y)
      .sort((a, b) => a.position.y - b.position.y);

    let adjustedContainers: any[] = [];
    let currentY = containerNode.position.y + newContainerHeight + EXPANDED_NODE_BUFFER;

    for (const container of containersBelow) {
      adjustedContainers.push({
        ...container,
        position: {
          ...container.position,
          y: currentY,
        },
      });
      currentY += container.data.height + EXPANDED_NODE_BUFFER;
    }

    // Finally: Combine all updates and set elements once
    const finalElements = [
      updatedContainer,
      ...adjustedContainers,
      ...elements
        .filter((item: any) => !allUpdatedNodes.some((upd: any) => upd.id === item.id))
        .filter((item: any) => item.id !== containerNode.id)
        .filter((item: any) => !adjustedContainers.some((adj: any) => adj.id === item.id)),
      ...allUpdatedNodes,
    ];

    setElements(finalElements);
  };

  const handleProcessExpand = (processId: string, isExpanded: string) => {
    const isCurrentlyExpanded = isExpanded === 'true';
    const targetNode = elements.find((item: any) => item.id === processId);
    const originalX = targetNode?.position.x;
    const originalY = targetNode?.position.y;
    const containerNode = elements.find((item: any) => item.id === targetNode?.parentNode);

    if (!targetNode || !containerNode) return;

    const nodesInSameColumn = elements.filter(
      (item: any) =>
        item?.parentNode === targetNode?.parentNode &&
        Math.abs(item?.position?.x - originalX) < 50 &&
        item?.id !== processId &&
        item?.position?.y > originalY,
    );

    const expandedHeight = countAllSteps(targetNode?.data?.steps) * 60 + EXPANDED_NODE_BUFFER;

    // Find all related nodes that need to be moved
    const relatedNodes = elements.filter((node: any) => {
      const isRelated = edges.some(
        (edge) =>
          (edge.source === processId && edge.target === node.id) ||
          (edge.target === processId && edge.source === node.id),
      );
      return isRelated && node.type === 'ProcessV2' && node.data.isExternal;
    });

    // Update positions of nodes below the expanded one
    const newNodes = nodesInSameColumn.map((node: any) => {
      const newY = !isCurrentlyExpanded ? node.position.y + expandedHeight : node.data.originalY;
      return {
        ...node,
        position: {
          ...node.position,
          y: newY,
        },
      };
    });

    const adjustedRelatedNodes = relatedNodes.map((node: any) => {
      return {
        ...node,
        position: {
          ...node.position,
          y: !isCurrentlyExpanded ? node.position.y + expandedHeight / 2.5 : node.data.originalY,
        },
      };
    });

    // Find the lowest node position after expansion
    const lowestNodeY =
      Math.max(
        ...newNodes.map((node) => node.position.y),
        !isCurrentlyExpanded ? targetNode.position.y + expandedHeight : targetNode.position.y,
      ) + 100; // Add padding

    if (containerNode) {
      let newHeight = containerNode.data.height;

      // Calculate new height when expanding/collapsing
      if (!isCurrentlyExpanded && lowestNodeY > containerNode.data.height) {
        newHeight = lowestNodeY;
      } else if (isCurrentlyExpanded) {
        const allNodesInContainer = elements.filter(
          (node) => node.parentNode === containerNode.id && node.id !== containerNode.id,
        );
        const minRequiredHeight =
          Math.max(
            ...allNodesInContainer.map(
              (node) => node.position.y + (node.data?.expand ? (node.data?.steps?.length || 0) * 40 : 0),
            ),
          ) + 100;
        newHeight = Math.max(containerNode.data.originalHeight, minRequiredHeight);
      }

      const updatedContainer = {
        ...containerNode,
        data: {
          ...containerNode.data,
          height: newHeight,
        },
      };

      let adjustedContainers: any = [];
      if (isCurrentlyExpanded) {
        // Only restore positions if there's no overlap
        const containersBelow = elements
          .filter((item) => item.id.startsWith('container-'))
          .filter((item) => item.position.y > containerNode.position.y)
          .sort((a, b) => a.position.y - b.position.y);

        // Check if we can restore positions without causing overlaps
        let currentY = containerNode.position.y + newHeight;
        let canRestore = true;

        for (const container of containersBelow) {
          if (container.data.originalY < currentY) {
            canRestore = false;
            break;
          }
          currentY = container.data.originalY + container.data.height;
        }

        if (canRestore) {
          adjustedContainers = containersBelow.map((item) => ({
            ...item,
            position: {
              ...item.position,
              y: item.data.originalY,
            },
          }));
        }
      } else {
        // When expanding, move containers below down if needed
        const overlappedContainers = elements
          .filter((item) => item.id.startsWith('container-'))
          .filter((item) => item.position.y > containerNode.position.y)
          .filter((item) => item.position.y < containerNode.position.y + newHeight);

        adjustedContainers = overlappedContainers.map((item) => ({
          ...item,
          position: {
            ...item.position,
            y: item.position.y + expandedHeight,
          },
        }));
      }

      setElements([
        updatedContainer,
        ...adjustedRelatedNodes,
        ...adjustedContainers,
        ...elements
          .filter((item) => !nodesInSameColumn.includes(item))
          .filter((item) => item.id !== containerNode.id)
          .filter((item) => !adjustedContainers.some((adj: any) => adj.id === item.id))
          .filter((item) => !adjustedRelatedNodes.some((adj: any) => adj.id === item.id)),
        ...newNodes,
      ]);
    } else {
      setElements([...elements.filter((item) => !nodesInSameColumn.includes(item)), ...newNodes]);
    }
  };

  const handleCategoryAdd = (categoryId: string) => {
    // DON'T clear query params - it triggers organizationId effect which wipes state!
    // clearQueryParams();

    // Add directly to categoriesToDraw
    setCategoriesToDraw((prev) => {
      if (!prev.includes(categoryId)) {
        return [...prev, categoryId];
      }
      return prev;
    });


  };

  const handleCategoryChipClick = async (category: { text: string; value: string }) => {
    const isCurrentlySelected = categoriesToDraw.includes(category.value);
    if (isCurrentlySelected) {
      // Remove from tracking states first
      setSelectedCategory((prev) => prev.filter((cat) => cat !== category.value));

      // Keep remaining categories to re-draw them
      const auxCategoriesToDraw = categoriesToDraw.filter((cat) => cat !== category.value);

      // Clear the current map
      clearCurrentMap();

      // Re-draw the remaining categories. We do this to make sure they re-position correctly
      for (const item of auxCategoriesToDraw) {
        await new Promise((resolve) => {
          setTimeout(() => {
            setCategoriesToDraw((prev) => [...prev, item]);
            resolve(void 0);
          }, 100);
        });
      }
    } else {
      // DON'T clear query params - it triggers organizationId effect which wipes state!
      // clearQueryParams();

      // Add directly to categoriesToDraw
      setCategoriesToDraw((prev) => [...prev, category.value]);
      setSelectedCategory((prev) => [...prev, category.value]);
    }
  };

  return (
    <Box className="relative w-full h-full">
      {/* Filters and search. This guy needs to be above the rest to avoid side effects on the diagram */}
      {/* TODO: The border color is just for development */}
      <Box
        data-tutorial-id="process-map-filters"
        className="absolute top-2 right-2 border border-solid border-gray-500 bg-gray-100 z-50 p-2 w-96 max-w-96"
        onClick={(e) => e.stopPropagation()}>
        <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg">
          <SearchFilterProcessMap toggleFilter={toggleMenu} clickResult={handleSearchResult} showMenu={showMenu} />
        </div>
        <Collapse in={showMenu}>
          <>
            <Box className="flex flex-wrap items-center mt-3 text-sm">
              <Typography variant="body14semibold" className="w-full">
                {localeCommon['area'] + ': '}
              </Typography>
              <WolfDropdown
                singleSelect={true}
                items={areaList.length > 0 ? areaList : [{ text: 'No areas to select', value: '', disabled: true }]}
                label={
                  !selectedAreaId
                    ? localeProcMap['selectArea']
                    : areas.find((item) => item.areaId === selectedAreaId)?.name
                }
                initialSelectedItems={areaList.map((item) => item.value)}
                showBorder={true}
                onChange={handleAreaChange}
              />
            </Box>

            {/* Only show service dropdown if an area is selected */}
            {selectedAreaId && (
              <Box className="flex flex-wrap items-center mt-3">
                <Typography variant="body14semibold" className="w-full">
                  {localeCommon['service'] + ': '}
                </Typography>
                <WolfDropdown
                  singleSelect={true}
                  items={
                    serviceList.length > 0
                      ? serviceList
                      : [{ text: 'No services to select', value: '', disabled: true }]
                  }
                  label={
                    !selectedServiceId
                      ? localeProcMap['selectService']
                      : services.find((item) => item.serviceId === selectedServiceId)?.name
                  }
                  initialSelectedItems={serviceList.map((item) => item.value)}
                  showBorder={true}
                  onChange={handleServiceChange}
                />
              </Box>
            )}

            {/* Only show category section if a service is selected */}
            {selectedServiceId && (
              <Box className="flex flex-wrap items-center mt-3">
                <Typography variant="body14semibold" className="w-full mb-2">
                  {localeCommon['category'] + ': '}
                </Typography>
                <Box className="flex flex-wrap gap-2">
                  {categoryList.length > 0 ? (
                    categoryList.map((category) => {
                      const isSelected = categoriesToDraw.includes(category.value);
                      return (
                        <WolfChip
                          key={category.value}
                          label={category.text}
                          onClick={() => handleCategoryChipClick(category)}
                          className="cursor-pointer shadow-lg"
                          selected={isSelected}
                        />
                      );
                    })
                  ) : (
                    <Typography variant="body14" className="text-gray-500 italic">
                      No categories available
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Here show currently drawn categories */}
            <Box className="mt-3">
              <Box className="flex items-center gap-1">
                <Typography variant="body14semibold">{localeProcMap['categoriesAdded']}</Typography>
                <WolfTooltip title={localeProcMap['categoriesAddedTooltip']}>
                  <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                </WolfTooltip>
              </Box>
              {drawnCategories.length === 0 && (
                <Box className="mt-2 w-100">
                  <Typography variant="body14">{localeProcMap['noCategoriesAddedYet']}</Typography>
                </Box>
              )}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={drawnCategories.map((cat) => cat.id)} strategy={verticalListSortingStrategy}>
                  {drawnCategories.map((item) => (
                    <Box key={item.id} className="my-1">
                      <SortableCategoryItem
                        id={item.id}
                        title={`${item.area} / ${item.service} / ${item.label}`}
                        onDelete={() => handleDeleteCategory(item.id)}
                      />
                    </Box>
                  ))}
                </SortableContext>
              </DndContext>
            </Box>
          </>
        </Collapse>
      </Box>
      <div className={`h-screen whitespace-break-spaces mb-28`}>
        <ReactFlow
          key={flowKey}
          nodes={elements}
          edges={edges}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          onPaneClick={onPaneClick}
          onEdgeClick={onEdgeClick}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}>
          <MiniMap />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      {elements.length === 0 && categoriesToDraw.filter(Boolean).length === 0 && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          {localeProcMap['selectAProcess']}
        </div>
      )}
    </Box>
  );
};

const ProcessMap: React.FC = () => {
  return (
    <ReactFlowProvider>
      <ProcessMapInner />
    </ReactFlowProvider>
  );
};

export default ProcessMap;
